import { DELETE_IDS } from "../@types/Order.type";
import { post as postEngine, getUserToken as getUserTokenV2 } from "../shared/utils/FetchV2.util";
import { decodeJwtPayload } from "../shared/auth/tokenRefresh";
import {
  del as delV2,
  get as getV2,
  getUserToken,
  patch as patchV2,
  post as postV2,
  put as putV2,
} from "../shared/utils/FetchV2.util";
import { orderRefreshMapper } from "../shared/utils/mappers/orderItems.mapper";
import { PAYMENT_METHOD, PLATFORM_CATALOG } from "../@types/Checkout.type";
import {
  PaymentIntent,
  ProcessV2Payment,
} from "../@types/Payments.types";
import { Token } from "../@types/Token.type";
import { SubscriptionType } from "../@types/Subscription";
import { SubscriptionPrebillingPreview } from "../@types/SubscriptionPrebilling.type";
import { SubscriptionWithPaymentInfo } from "../@types/SubscriptionWithPaymentInfo.type";
import { CartDetail } from "../@types/CartDetail.type";
import { OrderReceipt } from "../@types/OrderReceipts.type";
import { fetchSalesOverview } from "./salesOverview.service";

export const getSalesPeriodsAdquired = async ({
  tokenSession,
  regime,
  period_type,
}: {
  tokenSession?: string;
  regime?: string;
  period_type: "monthly" | "annual";
}) => {
  const qs = new URLSearchParams({ declaration_period: period_type });
  if (regime) qs.set("regime", regime);
  return getV2({
    service: "salesTransactionEngine",
    endpoint: `/periods/acquireds?${qs}`,
    options: sessionOptions(tokenSession),
  });
};

export const postCheckoutProcessPayPal = async (
  order_id: number,
  body: object
) => {
  try {
    // Directo al engine (api2 ya no lleva PayPal a sales). El user_id sale del JWT, como lo hacía api2.
    const userId = decodeJwtPayload(getUserTokenV2())?.sub;
    const response = await postEngine<any>({
      service: 'salesTransactionEngine',
      endpoint: `/payments/users/${userId}/shopping-carts/${order_id}/processv2/paypal`,
      body,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const putDefaultPaymentMethodStripeBilling = async (
  token_id: string,
  Subscription_id: number,
  tokenSession?: string
) => {
  return putV2({
    service: "salesTransactionEngine",
    endpoint: `/subscriptions/${Subscription_id}/payment-method/${token_id}`,
    body: {},
    options: cardsSession(tokenSession).options,
  });
};

export const postPaymentOnDemand = async (
  Subscription_id: number,
  tokenSession?: string
) => {
  try {
    const { options } = cardsSession(tokenSession);
    const response = await likeApi2(() =>
      postV2({
        service: "salesTransactionEngine",
        endpoint: `/subscriptions/${Subscription_id}/renew-attempt`,
        body: {},
        options,
      })
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const postOrders = async (body: object, tokenSession?: string) => {
  const { userId, options } = cardsSession(tokenSession);
  return postV2({
    service: "salesTransactionEngine",
    endpoint: `/shopping-carts/users/${userId}`,
    body,
    options,
  });
};

export const putCoupon = async (
  order_id: number,
  body: object,
  tokenSession?: string
) => {
  const { userId, options } = cardsSession(tokenSession);
  return patchV2({
    service: "salesTransactionEngine",
    endpoint: `/shopping-carts/${order_id}/users/${userId}/coupons`,
    body,
    options,
  });
};

export const deleteCartShoppingItems = async (
  items: DELETE_IDS[],
  cartId: number,
  tokenSession?: string
) => {
  const { userId, options } = cardsSession(tokenSession);
  return delV2({
    service: "salesTransactionEngine",
    endpoint: `/shopping-carts/${cartId}/users/${userId}/shopping-items/bulk`,
    body: items,
    options,
  });
};

// ORDERS
export const fetchPendingOrders = async (tokenSession?: string) => {
  const { userId, options } = cardsSession(tokenSession);
  return getV2({
    service: "salesTransactionEngine",
    endpoint: `/shopping-carts/users/${userId}/refresh?status=pending&is_renewal=false&shopping_cart_treatment=NORMAL`,
    options,
  });
};
export const getPendingOrderDetail = async (
  order: number,
  tokenSession?: string
) => {
  if (!order) return null;
  try {
    const { userId, options } = cardsSession(tokenSession);
    const response = await getV2({
      service: "salesTransactionEngine",
      endpoint: `/shopping-carts/${order}/users/${userId}/payment-detail`,
      options,
    });

    // El legacy manda expires_at como unix en string ("1787551199"); los
    // consumidores lo formatean con dayjs como fecha ISO. Normalizar aquí,
    // una sola vez, para las 3 superficies que lo leen.
    if (response?.expires_at && /^\d+$/.test(String(response.expires_at))) {
      response.expires_at = new Date(
        Number(response.expires_at) * 1000
      ).toISOString();
    }

    return response;
  } catch (error) {
    throw error;
  }
};

export const getOrderRefresh = async (
  params: string,
  _tokenSession?: string
) => {
  try {
    const response = await getV2({
      service: "salesTransactionEngine",
      endpoint: `/shopping-carts/refresh${params}`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const addItemBulkShoppingCart = async (
  cartId: number,
  body: object,
  _tokenSession?: string
) => {
  try {
    const response = await postV2({
      service: "salesTransactionEngine",
      endpoint: `/shopping-carts/${cartId}/shopping-items/bulk`,
      body,
    });
    return orderRefreshMapper([response]);
  } catch (error) {
    throw error;
  }
};

const sessionOptions = (tokenSession?: string) =>
  tokenSession
    ? { headers: { Authorization: `Bearer ${tokenSession}` } }
    : undefined;

// api2 respondía HTTP 200 con el error del backend en el body en processv2, checkout-session y
// renew-attempt, y el checkout lee los errores de esas tres así. Directo al engine se conserva esa
// forma: un 4xx/5xx regresa su body en vez de lanzar. Las demás (prepare, prebilling) ya daban el
// 4xx real y siguen lanzando.
const likeApi2 = async <T>(call: () => Promise<T>): Promise<T> => {
  try {
    return await call();
  } catch (error: any) {
    if (error?.response?.data) return error.response.data as T;
    throw error;
  }
};

const cardsSession = (tokenSession?: string) => {
  const authToken = tokenSession || getUserToken();
  const payload = authToken.split(".")[1] ?? "";
  const userId = Number(
    JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))).sub
  );
  return { userId, options: sessionOptions(tokenSession) };
};

export const getCreateIntent = async (
  userId: number,
  tokenSession?: string
) => {
  const response = await postV2<PaymentIntent>({
    service: "salesTransactionEngine",
    endpoint: "/payments/stripe/create-intent",
    body: { userId, newFlowStripe: true },
    options: cardsSession(tokenSession).options,
  });
  return response as PaymentIntent;
};

export const getPaymentCards = async (
  tokenSession?: string
): Promise<Token[]> => {
  const { userId, options } = cardsSession(tokenSession);
  return getV2<Token[]>({
    service: "salesTransactionEngine",
    endpoint: `/payments/tokens/users/${userId}`,
    options,
  });
};

export const deletePaymentCard = async (
  tokenId: string,
  tokenSession?: string
) => {
  const { userId, options } = cardsSession(tokenSession);
  return delV2({
    service: "salesTransactionEngine",
    endpoint: `/tokens/${tokenId}/users/${userId}`,
    options,
  });
};

export const postDefaultCard = async (pmId: string, tokenSession?: string) => {
  const { userId, options } = cardsSession(tokenSession);
  return putV2({
    service: "salesTransactionEngine",
    endpoint: `/tokens/users/${userId}/default/${pmId}`,
    body: {},
    options,
  });
};

export const sendStripeToken = async (
  paymentToken: string,
  tokenSession?: string
) => {
  return postV2({
    service: "salesTransactionEngine",
    endpoint: "/payments/tokens",
    body: { platform_id: 2, platform_token_id: paymentToken },
    options: cardsSession(tokenSession).options,
    skipAccountError: true,
  });
};

type TShoppingCartProcess = {
  cartId: number;
  tokenId: string;
  renewSubscription: boolean;
  additionalInformation: Record<string, any>;
  sourceChannel: string;
  paymentMethod: PAYMENT_METHOD;
  platformId: PLATFORM_CATALOG;
  tokenSession?: string;
  installments?: number;
};
type TShoppingCartPrepareProcessCreate = {
  shoppingCartId: number;
  tokenSession?: string;
  paymentMethod: PAYMENT_METHOD;
  platformId: PLATFORM_CATALOG;
};
type TShoppingCartPrepareProcessUpdate = {
  shoppingCartId: number;
  tokenSession?: string;
  tokenId: string;
  paymentMethod: PAYMENT_METHOD;
  platformId: PLATFORM_CATALOG;
};
export const postShoppingCartProcess = async ({
  cartId,
  tokenId,
  // renewSubscription = false,
  additionalInformation,
  sourceChannel,
  paymentMethod,
  platformId,
  installments,
}: TShoppingCartProcess) => {
  try {
    const { userId } = cardsSession();
    const response = await likeApi2(() =>
      postV2<ProcessV2Payment>({
        service: "salesTransactionEngine",
        endpoint: `/payments/users/${userId}/shopping-carts/${cartId}/processv2`,
        body: {
          token_id: tokenId,
          platform_id: platformId,
          payment_method: paymentMethod,
          source_channel: sourceChannel,
          additional_information: additionalInformation,
          ...(installments && { installments }),
        },
      })
    );
    return response as ProcessV2Payment;
  } catch (error) {
    throw error;
  }
};

// Apple Pay dentro del Checkout EMBEBIDO solo se puede presentar en Safari. Chrome en macOS
// anuncia capacidad de Apple Pay, así que Stripe pinta el botón, la hoja no abre y el cliente
// queda atrapado en el overlay sin poder pagar ni cerrar (reportado en prod el 23-sep-2026).
// `ApplePaySession` es API de WebKit y solo existe en Safari: en el Chrome de macOS sale
// `undefined` aunque `PaymentRequest` exista. El user agent NO sirve para esto, el de Chrome
// dice "Chrome" y "Safari" a la vez.
const browserCanPresentApplePay = () => {
  try {
    const w = window as unknown as { ApplePaySession?: unknown };
    return typeof window !== "undefined" && typeof w.ApplePaySession !== "undefined";
  } catch {
    return false;
  }
};

// Checkout embebido de Stripe para el carrito abierto del usuario. La sesión la arma el
// backend con la misma maquinaria del link de WhatsApp; aquí solo se pide el client_secret.
// Con `ui: 'hosted'` devuelve la `url` de la página de Stripe (la app Android la abre en Chrome).
export const postShoppingCartCheckoutSession = async (cartId: number, ui?: 'hosted') => {
  const { userId } = cardsSession();
  const response = await likeApi2(() =>
    postV2<{ client_secret?: string; url?: string; shopping_cart_id: number }>({
      service: "salesTransactionEngine",
      endpoint: `/payments/users/${userId}/shopping-carts/${cartId}/checkout-session`,
      body: { supports_apple_pay: browserCanPresentApplePay(), ...(ui && { ui }) },
    })
  );
  return response;
};

export const shoppingCartPrepareProcessCreate = async ({
  tokenSession,
  shoppingCartId,
  paymentMethod,
  platformId,
}: TShoppingCartPrepareProcessCreate) => {
  try {
    const { userId, options } = cardsSession(tokenSession);
    const response = await postV2<ProcessV2Payment>({
      service: "salesTransactionEngine",
      endpoint: `/payments/shopping-carts/${shoppingCartId}/users/${userId}/prepare-processv2`,
      body: {
        payment_method: paymentMethod,
        platform_id: platformId,
      },
      options,
    });
    return response as ProcessV2Payment;
  } catch (error) {
    throw error;
  }
};

export const shoppingCartPrepareProcessUpdate = async ({
  tokenSession,
  shoppingCartId,
  tokenId,
  paymentMethod,
  platformId,
}: TShoppingCartPrepareProcessUpdate) => {
  try {
    const { userId, options } = cardsSession(tokenSession);
    const response = await putV2({
      service: "salesTransactionEngine",
      endpoint: `/payments/shopping-carts/${shoppingCartId}/users/${userId}/prepare-processv2`,
      body: {
        payment_method: paymentMethod,
        platform_id: platformId,
        token_id: tokenId,
      },
      options,
    });
    return response;
  } catch (error) {
    throw error;
  }
};


/**
 * Detalle de un carrito desde heru-sales-transaction-engine. El backend resuelve
 * al dueño con el JWT y responde 404 si el carrito no es del usuario, así que no
 * se manda user_id. Sirve para reabrir una compra anterior sin depender del
 * `lastOrderPurchase` de localStorage.
 */
export const getShoppingCartDetail = async (
  cartId: number
): Promise<CartDetail> =>
  getV2({
    service: "salesTransactionEngine",
    endpoint: `/shopping-carts/${cartId}`,
  });

export const getReceipts = async (userId: number, tokenSession?: string) => {
  return getV2({
    service: "salesTransactionEngine",
    endpoint: `/receipts/users/${userId}`,
    options: sessionOptions(tokenSession),
  });
};

/**
 * El recibo de UN carrito. Sustituye a listar TODOS los del usuario y buscar el
 * que toca: medido contra prod con una cuenta de 98 recibos, la lista pesa
 * 6.5 MB y tarda ~3s; este pesa 67 KB y tarda ~0.5s. La pantalla de éxito lo
 * sondea cada 2s mientras el backend genera el recibo, así que la diferencia se
 * pagaba en cada intento.
 *
 * Cuando el recibo aún no existe el engine responde HTTP 200 con un cuerpo de
 * 404 adentro (paridad con api2), no un error. Por eso la decisión se toma
 * sobre `receipt_url`.
 */
export const getReceiptByCart = async (
  userId: number,
  shoppingCartId: number,
  tokenSession?: string
): Promise<OrderReceipt | null> => {
  const data = await getV2({
    service: "salesTransactionEngine",
    endpoint: `/receipts/users/${userId}/shopping-cart/${shoppingCartId}`,
    options: sessionOptions(tokenSession),
  });
  return data?.receipt_url ? (data as OrderReceipt) : null;
};

export const generateReceipt = async (
  userId: number,
  shoppingCartId: number,
  tokenSession?: string
) => {
  try {
    const response = await postV2({
      service: "salesTransactionEngine",
      endpoint: `/receipts/users/${userId}/shopping-cart/${shoppingCartId}`,
      body: {},
      options: sessionOptions(tokenSession),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const fetchAcquisitions = async () => {
  // Migrated to heru-sales-transaction-engine via the combined sales-overview
  // endpoint. No token needed — FetchV2 injects the JWT and the backend resolves
  // the user from it. Returns the legacy acquisition shape (see salesOverview.service).
  const { acquisitions } = await fetchSalesOverview();
  return acquisitions;
};


export const changeShoppingCartStatus = async ({
  shoppingCartId,
  status,
  tokenSession,
}: {
  shoppingCartId: number;
  status: "open" | "closed";
  tokenSession?: string;
}) => {
  const { userId, options } = cardsSession(tokenSession);
  return patchV2({
    service: "salesTransactionEngine",
    endpoint: `/shopping-carts/${shoppingCartId}/users/${userId}`,
    body: { status },
    options,
  });
};

export const getUserSuscriptions = async (): Promise<SubscriptionType[]> => {
  // Migrated to heru-sales-transaction-engine via the combined sales-overview
  // endpoint. No token needed — FetchV2 injects the JWT and the backend resolves
  // the user from it. Returns the legacy SubscriptionType shape (see salesOverview.service).
  const { subscriptions } = await fetchSalesOverview();
  return subscriptions;
};

export const getSubscriptionsEvents = async (
  id: number,
  tokenSession?: string
) => {
  return getV2({
    service: "salesTransactionEngine",
    endpoint: `/subscriptions/${id}/events`,
    options: cardsSession(tokenSession).options,
  });
};

export const cancelSubscription = async (id: number, tokenSession?: string) => {
  return delV2({
    service: "salesTransactionEngine",
    endpoint: `/subscriptions/${id}?source_channel=web`,
    options: cardsSession(tokenSession).options,
  });
};

export const pauseSubscription = async (
  subscriptionId: number,
  months: number,
  indefinite?: boolean,
  _tokenSession?: string
): Promise<{
  subscription_id: number;
  status: string;
  paused_until: string;
  months: number;
  indefinite: boolean;
}> => {
  return postV2({
    service: "salesTransactionEngine",
    endpoint: `/subscriptions/${subscriptionId}/pause`,
    body: { months, indefinite: !!indefinite },
  });
};

export const issueRetentionCoupon = async (
  subscriptionId: number,
  percent?: number
): Promise<{
  subscription_id: number;
  coupon_code: string;
  percent?: number;
}> => {
  return postV2({
    service: "salesTransactionEngine",
    endpoint: `/subscriptions/${subscriptionId}/retention-coupon${percent ? `?percent=${percent}` : ""}`,
    body: {},
  });
};

export const resumeSubscription = async (
  subscriptionId: number,
  _tokenSession?: string
): Promise<{
  subscription_id: number;
  status: string;
}> => {
  return postV2({
    service: "salesTransactionEngine",
    endpoint: `/subscriptions/${subscriptionId}/resume`,
    body: {},
  });
};

export const postRenewSubscription = async (
  subsId: any,
  tokenSession?: string
) => {
  try {
    return postV2({
      service: "salesTransactionEngine",
      endpoint: `/subscriptions/${subsId}/reactivate`,
      body: {},
      options: cardsSession(tokenSession).options,
    });
  } catch (error) {
    throw error;
  }
};

export const changeSubscription = async ({
  userId,
  subscriptionId,
  currentProductVersionId,
  newProductVersionId,
  tokenSession,
}: {
  userId: number;
  subscriptionId: number;
  currentProductVersionId: number;
  newProductVersionId: number;
  tokenSession?: string;
}) => {
  try {
    return postV2({
      service: "salesTransactionEngine",
      endpoint: `/subscriptions/${subscriptionId}/change-plan`,
      body: { userId, currentProductVersionId, newProductVersionId },
      options: cardsSession(tokenSession).options,
    });
  } catch (error) {
    throw error;
  }
};

export const prebillingSubscription = async (
  subscriptionId: number,
  tokenSession?: string
) => {
  try {
    const data = await postV2({
      service: "salesTransactionEngine",
      endpoint: `/subscriptions/${subscriptionId}/prebilling`,
      body: {},
      options: sessionOptions(tokenSession),
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const prebillingPreviewSubscription = async (
  subscriptionId: number,
  tokenSession?: string
): Promise<SubscriptionPrebillingPreview> => {
  try {
    const data = await postV2({
      service: "salesTransactionEngine",
      endpoint: `/subscriptions/${subscriptionId}/prebilling-preview`,
      body: {},
      options: sessionOptions(tokenSession),
    });
    return data.data;
  } catch (error) {
    throw error;
  }
};
export const prebillingAvailable = async (
  tokenSession?: string,
  declarationPeriod: "annual" | "monthly" = "annual"
) => {
  return getV2({
    service: "salesTransactionEngine",
    endpoint: `/subscriptions/prebilling-available?declaration_period=${declarationPeriod}`,
    options: sessionOptions(tokenSession),
  });
};
export const getSubscriptionWithPaymentInfo = async (
  subscriptionId: number,
  tokenSession?: string
): Promise<SubscriptionWithPaymentInfo> => {
  try {
    const { userId, options } = cardsSession(tokenSession);
    const data = await getV2<SubscriptionWithPaymentInfo>({
      service: "salesTransactionEngine",
      endpoint: `/subscriptions/${subscriptionId}/users/${userId}/with-payment-info`,
      options,
    });
    return data as SubscriptionWithPaymentInfo;
  } catch (error) {
    throw error;
  }
};

export const changeRenewalDate = async (
  subscriptionId: string,
  body: object,
  tokenSession?: string
) => {
  try {
    const data = await postEngine({
      service: 'salesTransactionEngine',
      endpoint: `/subscriptions/${subscriptionId}/change-billing-date`,
      body,
      options: tokenSession ? { headers: { Authorization: `Bearer ${tokenSession}` } } : undefined,
    });
    return data;
  } catch (error) {
    throw error;
  }
};
